import React, { useState, useEffect } from "react";
import { Menu, X, Edit3, Trash2, Plus, ArrowLeft } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// Dashboard Layout Components
import DashboardSidebar from "../../components/dashboard components/DashboardSidebar";
import DashboardHeader from "../../components/dashboard components/DashboardHeader";

// Project Form Components
import ProjectHomeTabForm from "../../components/dashboard components/forms/ProjectHomeTabForm";
import ProjectGalleryTabForm from "../../components/dashboard components/forms/ProjectGalleryTabForm";
import ProjectPlanForm from "../../components/dashboard components/forms/ProjectPlanForm";
import ProjectLocationForm from "../../components/dashboard components/forms/ProjectLocationForm";
import ProjectAmenitiesForm from "../../components/dashboard components/forms/ProjectAmenitiesForm";

// Use environment variable for API base
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const DraftProjectPage = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const [selectedProject, setSelectedProject] = useState(null);
  const [hasHomeRecord, setHasHomeRecord] = useState(false);

  const [projects, setProjects] = useState([]);
  const [newProjectName, setNewProjectName] = useState("");

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/admin/get-draft-project`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProjects(Array.isArray(res.data.drafts) ? res.data.drafts : []);
      } catch (err) {
        console.error("Error fetching Draft Projects:", err);
      }
    };
    fetchProjects();
  }, []);

  const handleSaveHome = async () => {
    if (!selectedProject?.id) return alert("No project selected.");

    const payload = {
      project_id: selectedProject.id,
      project_name: homeData.project_name || "",
      project_location: homeData.project_location || "",
      project_description: homeData.project_description || "",
      project_rera: homeData.project_rera || "",
      project_status: homeData.project_status || "Ongoing",
      project_extent: homeData.project_extent || "",
      project_badge_location: homeData.project_badge_location || "",
      project_type: homeData.project_type || "",
      project_price: homeData.project_price || "",
      project_complete_date: homeData.project_complete_date || "",
      approval: homeData.approval || "",
      hmda: homeData.hmda || "",
      features: homeData.features || "",
      project_image: homeData.project_image || "",
      brochure: homeData.brochure || "",
      gallery_description: homeData.project_description || "",
      gallery_images: homeData.gallery_images || [],
      project_plan_images: homeData.project_plan_images || [],
      project_amenities: homeData.project_amenities || [],
    };

    try {
      const token = localStorage.getItem("token");

      await axios.put(`${API_BASE_URL}/admin/update-project`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Home tab updated successfully!");
      const res = await axios.get(`${API_BASE_URL}/ongoing-projects/home`);
      setProjects(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error saving Home Tab:", error);
      alert(
        `Failed to save Home Tab: ${error.response?.data?.error || error.message}`,
      );
    }
  };

  const [homeData, setHomeData] = useState({
    project_name: "",
    project_location: "",
    project_description: "",
    project_rera: "",
    project_status: "",
    project_extent: "",
    project_badge_location: "",
    project_type: "",
    project_price: "",
    project_complete_date: "",
    approval: "",
    hmda: "",
    features: "",
    project_image: "",
    brochure: "",
    gallery_description: "",
    gallery_images: [],
    project_amenities: [],
    project_plan_images: [],
  });

  const [galleryData, setGalleryData] = useState({
    title: "",
    subtitle: "",
    images: [],
  });

  const [planData, setPlanData] = useState({ images: [] });
  const [locationData, setLocationData] = useState({ location: { map: null } });
  const [amenitiesData, setAmenitiesData] = useState([]);

  const calculateStatus = () => {
    let completed = 0;
    const total = 5;
    if (homeData?.title || homeData?.description) completed++;
    if (galleryData?.images?.length > 0) completed++;
    if (planData?.images?.length > 0) completed++;
    if (locationData?.location?.map) completed++;
    if (amenitiesData?.length > 0) completed++;
    return Math.round((completed / total) * 100);
  };

  const loadHomeDetail = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API_BASE_URL}/admin/get-draft-project/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const p = res.data?.draft;

      setHomeData({
        project_name: p.project_name || "",
        project_status: p.project_status || "",
        project_description: p.project_description || "",
        project_rera: p.project_rera || "",
        project_extent: p.project_extent || "",
        project_location: p.project_location || "",
        project_badge_location: p.project_badge_location || "",
        project_type: p.project_type || "",
        project_price: p.project_price || "",
        project_complete_date: p.project_complete_date || "",
        approval: p.approval || "",
        hmda: p.hmda || "",
        features: p.features || "",
        brochure: p.brochure || "",
        project_image: p.project_image || "",
        gallery_description: p.project_description || "",
        gallery_images: p.gallery_images || [],
        project_plan_images: p.project_plan_images || [],
        project_amenities: p.project_amenities || [],
      });

      setHomeData(p.draft_data);
      setHasHomeRecord(true);
    } catch (e) {
      setHasHomeRecord(false);
    }
  };

  const handleSave = async (sectionName, data) => {
    if (!selectedProject?.id) {
      alert("No project selected!");
      return;
    }

    try {
      const status = calculateStatus();

      await axios.put(
        `${API_BASE_URL}/ongoing-projects/home/${selectedProject.id}`,
        {
          project_title: homeData.title || selectedProject.name,
          status: status.toString(),
          description: homeData.description || "",
          rera: homeData.rera || "",
          location: homeData.location || "",
          extent: homeData.extent || "",
          brochure: homeData.brochure || "",
          image: homeData.mainImage || null,
        },
      );

      setProjects((prev) =>
        prev.map((p) =>
          p.project_id === selectedProject.id ? { ...p, status } : p,
        ),
      );
      alert(`${sectionName} saved successfully! (Progress: ${status}%)`);
    } catch (error) {
      console.error(`Error saving ${sectionName}:`, error);
      alert("Failed to save section.");
    }
  };

  const handleEditProject = async (project) => {
    const id = project.draft_id;

    setSelectedProject({
      ...project.draft_data,
      name:
        project.draft_data.project_title ||
        project.draft_data.project_name ||
        project.draft_data.name ||
        "Untitled Project",
      id,
    });

    setActiveTab("home");

    await loadHomeDetail(id);
  };

  const handlePublishProject = async (draftId) => {
    try {
      const token = localStorage.getItem("token");

      if (!window.confirm("Are you sure you want to publish this project?"))
        return;

      const res = await axios.post(
        `${API_BASE_URL}/admin/publish-draft-project/${draftId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data.success) {
        alert("Project published successfully!");
      }
      setProjects(projects.filter((p) => p.draft_id !== draftId));
    } catch (error) {
      console.error("Publish error:", error);
      alert("Failed to publish project.");
    }
  };

  const handleBackToList = () => {
    setSelectedProject(null);
    setHomeData({
      project_name: "",
      project_location: "",
      project_description: "",
      project_rera: "",
      project_status: "",
      project_extent: "",
      project_badge_location: "",
      project_type: "",
      project_price: "",
      project_complete_date: "",
      approval: "",
      hmda: "",
      features: "",
      project_image: "",
      brochure: "",
      gallery_description: "",
      gallery_images: [],
      project_amenities: [],
      project_plan_images: [],
    });
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm("Are you sure you want to delete this draft project?"))
      return;

    try {
      const token = localStorage.getItem("token");

      await axios.delete(`${API_BASE_URL}/admin/delete-draft-project/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setProjects((prev) => prev.filter((p) => p.draft_id !== id));

      alert("Draft project deleted successfully!");
    } catch (error) {
      console.error("Delete error:", error);
      alert(error.response?.data?.message || "Failed to delete draft project.");
    }
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case "home":
        return (
          <ProjectHomeTabForm
            homeData={homeData}
            setHomeData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
          />
        );

      case "gallery":
        return (
          <ProjectGalleryTabForm
            galleryData={homeData}
            setGalleryData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
          />
        );

      case "plan":
        return (
          <ProjectPlanForm
            lightboxData={homeData}
            setLightboxData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
          />
        );

      case "location":
        return (
          <ProjectLocationForm
            placeholderData={homeData}
            setPlaceholderData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
          />
        );

      case "amenities":
        return (
          <ProjectAmenitiesForm
            amenitiesData={homeData}
            setAmenitiesData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
          />
        );

      default:
        return null;
    }
  };

  const TabButton = ({ id, label }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-5 py-2 text-sm md:text-base font-semibold border-b-4 transition-all duration-300 ${
        activeTab === id
          ? "border-blue-600 text-blue-700 bg-blue-50"
          : "border-transparent text-gray-600 hover:text-blue-600 hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      <div className="hidden lg:block">
        <DashboardSidebar />
      </div>

      <div
        className={`fixed inset-y-0 left-0 bg-white w-72 shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:hidden`}
      >
        <DashboardSidebar />
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-blue-600"
          onClick={() => setSidebarOpen(false)}
        >
          <X size={28} />
        </button>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      <main className="flex-1 flex flex-col w-full">
        <div className="sticky top-0 bg-white z-30 shadow-sm flex items-center justify-between px-4 sm:px-8 py-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-700 hover:text-blue-600"
          >
            <Menu size={28} />
          </button>

          <div className="flex-1 flex justify-center">
            <DashboardHeader
              title={
                selectedProject
                  ? `${selectedProject.name} - Project Management`
                  : "Draft Projects"
              }
              backTo="/dashboard"
            />
          </div>
        </div>

        {!selectedProject ? (
          <div className="p-10">
            <div className="flex flex-wrap items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-blue-700">
                Draft Projects
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 bg-white rounded-xl shadow-sm">
                <thead>
                  <tr className="bg-gray-100 text-left text-gray-700">
                    <th className="px-6 py-3 text-lg font-semibold">Name</th>
                    <th className="px-6 py-3 text-lg font-semibold">Status</th>
                    <th className="px-6 py-3 text-lg font-semibold">Actions</th>
                    <th className="px-6 py-3 text-lg font-semibold">
                      Publish Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project, index) => {
                    const id = project.draft_id;
                    const name =
                      project.draft_data.project_title ||
                      project.draft_data.project_name ||
                      project.draft_data.name ||
                      "Untitled Project";
                    const status =
                      project.draft_data.status ||
                      project.draft_data.project_status;

                    return (
                      <tr
                        key={`${project.draft_id}-${index}`}
                        className="border-t hover:bg-gray-50 transition"
                      >
                        <td className="px-6 py-4 text-gray-800 text-lg font-medium">
                          {name || "Untitled Project"}
                        </td>

                        <td className="px-6 py-4">
                          {typeof status === "number" ? (
                            <span
                              className={`text-white text-sm px-4 py-2 rounded-lg font-semibold ${
                                status === 100
                                  ? "bg-green-600"
                                  : status >= 70
                                    ? "bg-yellow-500"
                                    : "bg-gray-400"
                              }`}
                            >
                              {status}%
                            </span>
                          ) : status ? (
                            <span className="inline-flex px-4 py-2 rounded-lg bg-green-100 text-green-700 text-sm font-semibold">
                              {status}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">N/A</span>
                          )}
                        </td>

                        <td className="px-6 py-4 flex items-center space-x-4">
                          <button
                            onClick={() => handleEditProject(project)}
                            className="text-gray-700 hover:text-blue-600 transition"
                            title="Edit Project"
                          >
                            <Edit3 size={22} />
                          </button>

                          <button
                            onClick={() => handleDeleteProject(id)}
                            className="text-red-500 hover:text-red-700 transition"
                            title="Delete Project"
                          >
                            <Trash2 size={22} />
                          </button>
                        </td>

                        <td className="px-6 py-4">
                          <button
                            onClick={() =>
                              handlePublishProject(project.draft_id)
                            }
                            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm hover:shadow-md transition-all duration-200"
                            title="Publish Project"
                          >
                            Publish
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="p-4 sm:p-8 md:p-10 max-w-6xl mx-auto w-full bg-white mt-4 sm:mt-6 rounded-xl shadow-md">
            <div className="flex justify-start mb-8">
              <button
                onClick={handleBackToList}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow transition"
              >
                <ArrowLeft size={18} />
                Back to Projects
              </button>
            </div>

            <div className="bg-white border-b border-gray-200 shadow-sm mb-6">
              <div className="flex justify-center gap-2 sm:gap-4 px-2 sm:px-4 py-2 flex-wrap">
                <TabButton id="home" label="Home" />
                <TabButton id="gallery" label="Gallery" />
                <TabButton id="plan" label="Project Plan" />
                <TabButton id="location" label="Location Map" />
                <TabButton id="amenities" label="Amenities" />
              </div>
            </div>

            {renderActiveTab()}
          </div>
        )}
      </main>
    </div>
  );
};

export default DraftProjectPage;
